import { actionAlert } from "@suraasa/placebo-ui"
import { useQuery } from "@tanstack/react-query"
import api from "api"
import queries from "api/resources/orders/queries"
import { RegistrationOrderStatus } from "api/resources/orders/types"
import { SavedAddress } from "api/resources/payment/types"
import ErrorPage from "components/ErrorPage"
import FullPageLoading from "components/FullPageLoading"
import TopNavigation from "components/Navigation/TopNavigation"
import { AuthManager } from "features/Authentication/AuthManager"
import { useGlobalContext } from "global/Context/context"
import { useEffect, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import useAuthCodeProcessor from "utils/hooks/useAuthCodeProcessor"
import useHideGlobalBottomNavigation from "utils/hooks/useGlobalHideBottomNavigation"
import useHideTopNavigation from "utils/hooks/useHideTopNavigation"

import PaymentSuccess from "../PaymentSuccess"
import {
  PGCTL_PRODUCT_REGISTRATION_AMOUNTS,
  trackPgctlRegistrationEnded,
  trackPgctlRegistrationStarted,
} from "../tracking"
import BillingForm from "./BillingForm"
import OrderSummary from "./OrderSummary"

const PgctlPaymentPage = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const autoTriggerPayment = searchParams.get("autoPay") === "1"
  const isFromLandingPage = searchParams.get("source") === "landing-page"
  const productSlug = searchParams.get("product")
  useHideTopNavigation()
  useHideGlobalBottomNavigation()

  const { isAuthenticated } = useGlobalContext()

  useAuthCodeProcessor({ shouldRedirectToLogin: false })

  const existingOrder = useQuery({
    queryFn: () => {
      if (!productSlug) {
        throw Error("Product slug not found.")
      }
      if (!isAuthenticated) {
        return Promise.resolve(null)
      }
      return api.orders.listOrders({ params: { product_slug: productSlug } })
    },
    queryKey: queries.list({ page: 1 }).queryKey,
    enabled: !!productSlug && isAuthenticated,
    select: data => data?.data?.[0],
  })

  const product = useQuery({
    queryFn: () =>
      api.products.getProduct({
        urlParams: { productSlug: productSlug || "" },
        params: {
          fields: ["registration_fee", "prices"],
        },
      }),

    queryKey: ["product", productSlug],
  })

  const trackingStartedRef = useRef(false)
  useEffect(() => {
    if (!isFromLandingPage) return
    if (trackingStartedRef.current) return
    if (!productSlug) return
    trackingStartedRef.current = true

    const authInfo = AuthManager.getPlatformAuth()
    const email = searchParams.get("email") || authInfo?.user.email || ""
    const totalAmount = PGCTL_PRODUCT_REGISTRATION_AMOUNTS[productSlug] ?? 0

    trackPgctlRegistrationStarted({ email, totalAmount })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productSlug, isFromLandingPage])

  const trackingFailedRef = useRef(false)

  useEffect(() => {
    if (!isFromLandingPage) return
    const redirectStatus = searchParams.get("redirect_status")
    if (!redirectStatus || redirectStatus === "succeeded") return
    if (trackingFailedRef.current) return
    trackingFailedRef.current = true

    const authInfo = AuthManager.getPlatformAuth()
    const email = searchParams.get("email") || authInfo?.user.email || ""
    const status =
      redirectStatus === "canceled" || redirectStatus === "cancelled"
        ? "cancelled"
        : "failed"

    trackPgctlRegistrationEnded({
      email,
      transactionId: searchParams.get("payment_intent") || "",
      status,
    })
  }, [searchParams, isFromLandingPage])

  useEffect(() => {
    const showAlertAndReload = async () => {
      if (searchParams.get("redirect_status") === "succeeded") {
        const newParams = new URLSearchParams(searchParams)
        newParams.delete("redirect_status")
        newParams.delete("paymentPlanId")
        newParams.delete("payment_intent")
        newParams.delete("payment_intent_client_secret")
        setSearchParams(newParams)

        await actionAlert.success({
          title: "Payment Successful!",
          duration: 5000,
          onClose: () => {},
        })

        window.location.reload()
      }
    }
    showAlertAndReload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [address, setAddress] = useState<SavedAddress | null>(null)

  useEffect(() => {
    existingOrder.refetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated])

  if (existingOrder.isLoading) {
    return <FullPageLoading />
  }

  if (!productSlug || product.isError) {
    return <ErrorPage errorStatus={404} title="Product Not Found" />
  }

  if (
    existingOrder.data?.registrationOrder?.status ===
      RegistrationOrderStatus.SUCCESSFUL &&
    existingOrder.data?.id
  ) {
    return (
      <PaymentSuccess
        orderId={existingOrder.data?.id}
        productSlug={productSlug}
        isFromLandingPage={isFromLandingPage}
      />
    )
  }

  return (
    <>
      <TopNavigation />
      <div className="flex w-full flex-col sm:min-h-screen md:flex-row">
        <div className="flex-1 bg-white">
          <BillingForm
            onAddressCreated={value => {
              setAddress(value)
            }}
          />
        </div>
        <div className="hidden flex-1 bg-surface-50 sm:block">
          <OrderSummary
            onPaymentSuccess={() => {
              existingOrder.refetch()
            }}
            address={address}
            autoTriggerPayment={autoTriggerPayment}
          />
        </div>
      </div>
      <div className="block flex-1 bg-surface-50 sm:hidden">
        <OrderSummary
          onPaymentSuccess={() => {
            existingOrder.refetch()
          }}
          address={address}
          autoTriggerPayment={autoTriggerPayment}
        />
      </div>
    </>
  )
}

export default PgctlPaymentPage
